import {
  PaymentReviewFailureErrorCode,
  PaymentReviewRecordStatus,
} from "@/models/operations/paymentReviewRecord/paymentReviewRecord.types"
import {
  FilterCheckbox,
  FilterDateRange,
  FilterMoneyRange,
  FilterRadio,
  FilterType,
  SoDataTableColumnType,
  SoDataTableHeader,
} from "@/types/components/SoDataTable"
import { SoRadioCardChartType } from "@/types/components/SoRadioCardChart"

export const MAX_SELECTED_RECORDS = 5000

export enum ReprocessMethod {
  ByFilters = "REPROCESS_BY_FILTERS",
  ByIds = "REPROCESS_BY_IDS",
}

export const editableStatuses: PaymentReviewRecordStatus[] = [
  "failed",
  "skipped",
]

export const statuses = {
  completed: "Completed",
  failed: "Failed",
  skipped: "Skipped",
  pending: "Pending",
  running: "Running",
  retry: "Retry",
}

export type TopFilter = {
  name: string
  label: string
  type: SoRadioCardChartType
  percentage?: number
  count?: number
}

export const topFilters: TopFilter[] = [
  {
    name: "completed",
    label: "Completed reviews",
    type: "normal",
  },
  {
    name: "failed",
    label: "Failed reviews",
    type: "critical",
  },
  {
    name: "skipped",
    label: "Skipped reviews",
    type: "caution",
  },
  {
    name: "total",
    label: "Total records",
    type: "neutral",
  },
]

export const tableHeaders: SoDataTableHeader[] = [
  {
    title: "ID",
    key: "id",
  },
  {
    title: "Account",
    key: "accountNumber",
    sortKey: "account_number",
    type: SoDataTableColumnType.COPY_TO_CLIPBOARD,
    sortable: false,
  },
  {
    title: "Status",
    key: "status",
    sortKey: "status",
    type: SoDataTableColumnType.STATUS,
  },
  {
    title: "Reason",
    key: "failureReasonMapped",
    sortable: false,
  },
  {
    title: "Date",
    key: "createdAt",
    sortKey: "created_at",
    type: SoDataTableColumnType.DATE,
  },
  {
    title: "Last retry",
    key: "lastRetryTime",
    sortKey: "last_retry_time",
    type: SoDataTableColumnType.DATE,
    fallbackValue: "-",
  },
  {
    title: "Next Payment",
    key: "nextPaymentDate",
    sortKey: "next_payment_date",
    type: SoDataTableColumnType.DATE,
    fallbackValue: "-",
  },
  {
    title: "Current",
    key: "paymentSchedulePeriodAmount",
    sortKey: "payment_schedule_period_amount",
    type: SoDataTableColumnType.MONEY,
    align: "end",
    fallbackValue: "-",
  },
  {
    title: "Suggested",
    key: "newMonthlyAmount",
    sortKey: "new_monthly_amount",
    type: SoDataTableColumnType.MONEY,
    align: "end",
    fallbackValue: "-",
  },
  {
    title: "Change",
    key: "changedAmount",
    sortKey: "changed_amount",
    type: SoDataTableColumnType.MONEY_ARROW,
    align: "end",
    fallbackValue: "-",
  },
  {
    title: "Balance",
    key: "accountBalance",
    sortKey: "account_balance",
    type: SoDataTableColumnType.MONEY_COLORED,
    align: "end",
    fallbackValue: "-",
  },
  {
    title: "Annual cost",
    key: "estimateAnnualCost",
    sortKey: "estimate_annual_cost",
    type: SoDataTableColumnType.MONEY,
    align: "end",
    fallbackValue: "-",
  },
]

export type TableFilters = {
  status: FilterRadio<PaymentReviewRecordStatus>
  reason: FilterCheckbox<PaymentReviewFailureErrorCode>
  date: FilterDateRange
  changedAmount: FilterMoneyRange
  nextPaymentDate: FilterDateRange
  lastRetryDate: FilterDateRange
}

export const tableFilters: TableFilters = {
  status: {
    type: FilterType.RADIO,
    label: "Status",
    value: null,
    options: statuses,
    expandable: true,
  },
  reason: {
    type: FilterType.CHECKBOX,
    label: "Reason",
    options: {}, // calculated value
    value: [],
    expandable: true,
  },
  date: {
    type: FilterType.DATE_RANGE,
    label: "Date",
    value: { from: null, to: null },
    expandable: true,
  },
  changedAmount: {
    type: FilterType.MONEY_RANGE,
    label: "Change",
    value: { min: null, max: null },
    expandable: true,
  },
  nextPaymentDate: {
    type: FilterType.DATE_RANGE,
    label: "Next Payment Date",
    value: { from: null, to: null },
    expandable: true,
  },
  lastRetryDate: {
    type: FilterType.DATE_RANGE,
    label: "Last Retry Date",
    value: { from: null, to: null },
    expandable: true,
  },
}
